import { Dialog, DialogTrigger, Popover } from 'react-aria-components'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from './button'
import { Checkbox } from './checkbox'

export interface MultiSelectFilterProps<T extends string> {
  label: string
  options: readonly { id: T; label: string }[]
  value: readonly T[]
  onChange: (value: T[]) => void
  isDisabled?: boolean
}

export function MultiSelectFilter<T extends string>({ label, options, value, onChange, isDisabled }: MultiSelectFilterProps<T>) {
  const { t } = useTranslation()
  return (
    <DialogTrigger>
      <Button variant="secondary" size="small" isDisabled={isDisabled}>
        {label}
        {value.length > 0 && <span className="rounded bg-(--color-primary-muted) px-1.5 text-(--color-primary)">{value.length}</span>}
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </Button>
      <Popover placement="bottom start" offset={6}
        className="z-50 w-64 max-w-[calc(100vw-2rem)] rounded-xl border border-(--color-border) bg-(--color-surface) p-3 shadow-lg">
        <Dialog aria-label={label} className="outline-none">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="text-sm font-semibold">{label}</span>
            <Button variant="ghost" size="small" isDisabled={!value.length} onPress={() => onChange([])}>{t('actions.clear')}</Button>
          </div>
          <div className="max-h-64 space-y-3 overflow-y-auto pb-1">
            {options.map(option => <Checkbox key={option.id} label={option.label}
              isSelected={value.includes(option.id)}
              onChange={selected => onChange(selected ? [...value, option.id] : value.filter(id => id !== option.id))} />)}
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
}
